import agg from '@/methods/ag_grid_toolbox';

export default {

    /**
     * Just a test function that creates and then downloads some really boring CSV.
     */
    writeTestFile: () => {
        const rows = [
            ["name1", "city1", "some other info"],
            ["name2", "city2", "more info"]
        ];

        let csvContent = "data:text/csv;charset=utf-8,"
            + rows.map(e => e.join(",")).join("\n");
        downloadCSV(csvContent);
    },

    /**
     * Converts a JSON Object to CSV format (with a chosen seperator, since commas might be inadequate).
     * @param {Object} obj - Object to convert to CSV string
     * @param {Object[]} desired_properties - List of keys to be considered for the CSV export
     *      Must be in the format {title, id, path, checked} (see e.g. Variant_SearchResults.vue)
     * @param {String} seperator - Seperator character
     */
    convertJSONToCSV: async (obj, desired_properties, seperator) => {
        if (!desired_properties || desired_properties.length === 0) return;
        let csv_string = "";
        csv_string += desired_properties.map((property) => property.id).join(seperator) + '\n';
        let stop_key = (desired_properties.slice(-1)[0]).id;
        return new Promise((resolve, reject) => {
            try {
                for (const [_, entry] of Object.entries(obj)) {
                    for (let property of desired_properties) {
                        var content = agg.resolveDeepProperty(property.path, entry);
                        if (!content) content = '';
                        if (Array.isArray(content)) csv_string += content.length.toString();
                        else {
                            if (typeof content === 'string' || content instanceof String) {
                                if (content.includes(seperator)) {
                                    content = content.replaceAll(seperator, '$');
                                }
                            }
                            csv_string += content;
                        }
                        csv_string += (property.id == stop_key ? '\n' : seperator);
                    }
                }
                resolve(csv_string);
            }
            catch (e) {
                reject(e);
            }
        });
    },

    /**
     * Converts an AG Grid Table to CSV format (going by the row data object that is used for rendering)
     * @param {Object} row_data - Row data as prepared for AG Grid
     * @param {String} seperator - Seperator character
     */
    convertAGGTableToCSV: (row_data, seperator) => {
        let csv_string = "data:text/csv;charset=utf-8,";
        csv_string += Object.keys(row_data[0]).join(seperator) + '\n';
        for (let row of row_data) {
            var stop_key = Object.keys(row_data[0])[Object.keys(row_data[0]).length - 1];
            Object.keys(row).forEach((k) => {
                csv_string += JSON.stringify(row[k]);
                if (k != stop_key) csv_string += seperator;
                else csv_string += '\n';
            })
        }
        return csv_string;
    },

    /**
     * Compiles a list of treatments going from an Onkopus variants object, then converts them to CSV.
     * Separate function because the structure is so fundamentally different from a basic JSON object
     * @param {Object} obj - Onkopus variants object (by default store.getters.getVariants)
     * @param {Object[]} desired_properties - List of keys to be considered for the CSV export
     *      Must be in the format {title, id, path, checked} (see e.g. Variant_SearchResults.vue)
     * @param {Number} granularity - 0 to 3, where 0 only gives exact matches and 3 gives everything.
     * @param {String} seperator - Seperator character
     */
    convertTreatmentsToCSV: async (obj, desired_properties, granularity, seperator) => {
        if (!desired_properties || desired_properties.length === 0) return;
        let csv_string = "";
        let sub_keys = ['exact_match', 'same_position', 'same_position_any_mutation', 'any_mutation_in_gene']
        .slice(granularity, granularity + 1);
        csv_string += desired_properties.map((property) => property.id).join(seperator) + '\n';
        let stop_key = (desired_properties.slice(-1)[0]).id;
        return new Promise((resolve, reject) => {
            try {
                for (const [_, variant] of Object.entries(obj)) {
                    // For each variant, we have a list of potential treatments
                    // They are divided into four categories
                    // The granularity parameter chooses which of them to consider
                    if ("onkopus_aggregator" in variant) {
                        var variant_treatments = variant.onkopus_aggregator.merged_match_types_data;
                        //Object.keys(variant_treaments).filter((k) => sub_keys.includes(k)).forEach((key) => {
                            var treatment_object = variant_treatments
                            //var treatment_object = variant_treaments[key];
                            treatment_object.forEach((treatment) => {
                                // Sometimes drugs is an array, sometimes it isnt?
                                // Pretty dodgy solution but IDK
                                if (!Array.isArray(treatment.drugs)) treatment.drugs = [{drug_name: treatment.drugs}];
                                    var treatment_drug_label = ""
                                    var treatment_drug_class_label = ""
                                    treatment.drugs.forEach((treatment_drug) => {
                                        for (let property of desired_properties) {
                                            if (property.id == 'drug_name') {
                                                treatment_drug_label += treatment_drug[property.path] + "; "

                                                if ("drug_classes" in variant) {
                                                    if (treatment_drug[property.path].toLowerCase() in variant["drug_classes"]) {
                                                        for (let drug_c in variant["drug_classes"][treatment_drug[property.path].toLowerCase()]) {
                                                            treatment_drug_class_label += variant["drug_classes"][treatment_drug[property.path].toLowerCase()][drug_c] + "" + ", "
                                                        }
                                                        treatment_drug_class_label = treatment_drug_class_label.slice(0, -2)
                                                        treatment_drug_class_label += "; "
                                                    }
                                                }
                                            }
                                        }
                                    })
                                    treatment_drug_label = treatment_drug_label.slice(0, -2)
                                    treatment_drug_class_label = treatment_drug_class_label.slice(0, -2)

                                    for (let property of desired_properties) {
                                        if (property.id == 'drug_name') {
                                            csv_string += "\"" +  treatment_drug_label + "\""
                                        } else if (property.id == 'drug_class') {
                                            csv_string += "\"" + treatment_drug_class_label + "\""
                                        }
                                        else {
                                            var content = agg.resolveDeepProperty(property.path, treatment);
                                            if (!content) content = '';
                                            if (Array.isArray(content)) csv_string += content.length.toString();
                                            else csv_string += content;
                                        }
                                        csv_string += (property.id == stop_key ? '\n' : seperator);
                                    }

                            })
                        //})
                    }
                }
                resolve(csv_string);
            }
            catch (e) {
                reject(e);
            }
        });
    },

    /**
     * Encodes a CSV string and provides it as a download to the user.
     * This is done using a pseudo a element.
     * @param {String} csv - String to export as CSV file
     * @param {String} filename - Name of the file (without extension)
     */
    downloadCSV: (csv, filename) => {
        if (!csv) return;
        var link = document.createElement("a");
        const blob = new Blob([csv], { type: 'text/csv' });
        var url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename + ".csv");
        document.body.appendChild(link);

        link.click();
    },
    
}
